import React, { useState, useEffect, useMemo } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  DollarSign,
  Edit,
  Save,
  AlertCircle,
  RefreshCw,
  Calculator,
  Image,
  Video,
  Calendar,
  MousePointerClick,
  Eye,
  Layers
} from 'lucide-react';
import { calculatePromotionServiceFee, formatPrice } from '../../../utils/promotionPricing';
import {
  createAdminPromotionPricingConfig,
  getAdminPromotionPricingConfigs,
  updateAdminPromotionPricingConfig,
} from '../../../services/adminApi';

const PRICING_MODES = [
  { value: 'combined', label: 'Combiné', hint: 'Calendrier + durée d\'affichage + engagement' },
  { value: 'calendar_only', label: 'Calendrier uniquement', hint: 'Forfait jour / période' },
  { value: 'seconds_only', label: 'Durée (secondes) uniquement', hint: 'Forfait de base + secondes supplémentaires' },
  { value: 'engagement_only', label: 'Engagement uniquement', hint: 'Base + clics et vues estimés' }
];

const DISPLAY_FORMAT_PRESETS = [
  {
    display_format: 'floating_left',
    label: 'Flottant a gauche',
    defaults: {
      description: 'Carte promotionnelle flottante sur les pages liste/detail.',
      media_type: 'image',
      base_price: 10,
      price_per_day: 2,
      price_per_click: 0.05,
      price_per_view: 0.005,
      price_per_second: 0.02,
      min_duration_seconds: 5,
      max_duration_seconds: 30,
      pricing_mode: 'combined',
      is_active: true,
    },
  },
  {
    display_format: 'top_banner',
    label: 'Bande en haut',
    defaults: {
      description: 'Banniere hero visible en haut des pages principales.',
      media_type: 'video',
      base_price: 25,
      price_per_day: 6,
      price_per_click: 0.08,
      price_per_view: 0.01,
      price_per_second: 0.06,
      min_duration_seconds: 8,
      max_duration_seconds: 45,
      pricing_mode: 'combined',
      is_active: true,
    },
  },
  {
    display_format: 'section',
    label: 'Section promotions',
    defaults: {
      description: 'Bloc promotions dans la section dediee.',
      media_type: 'image',
      base_price: 15,
      price_per_day: 3,
      price_per_click: 0.06,
      price_per_view: 0.006,
      price_per_second: 0.03,
      min_duration_seconds: 5,
      max_duration_seconds: 30,
      pricing_mode: 'combined',
      is_active: true,
    },
  },
];

const AdminPromotionPricing = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingFormat, setCreatingFormat] = useState('');
  const [error, setError] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    base_price: 0,
    price_per_day: 0,
    price_per_click: 0,
    price_per_view: 0,
    price_per_second: 0,
    min_duration_seconds: 5,
    max_duration_seconds: 30,
    pricing_mode: 'combined',
    description: '',
    media_type: null,
    is_active: true
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await getAdminPromotionPricingConfigs();
      setConfigs(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des configurations:', err);
      setError(err?.message || 'Erreur lors du chargement des configurations');
    } finally {
      setLoading(false);
    }
  };

  const parseNum = (v, fallback = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const parseIntSafe = (v, fallback = null) => {
    if (v === '' || v === null || v === undefined) return fallback;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const handleEdit = (config) => {
    setEditingConfig(config.id);
    setFormData({
      base_price: parseNum(config.base_price),
      price_per_day: config.price_per_day != null ? parseNum(config.price_per_day) : 0,
      price_per_click: config.price_per_click != null ? parseNum(config.price_per_click) : 0,
      price_per_view: config.price_per_view != null ? parseNum(config.price_per_view) : 0,
      price_per_second: config.price_per_second != null ? parseNum(config.price_per_second) : 0,
      min_duration_seconds: parseIntSafe(config.min_duration_seconds, 5),
      max_duration_seconds: parseIntSafe(config.max_duration_seconds, 30),
      pricing_mode: config.pricing_mode || 'combined',
      description: config.description || '',
      media_type: config.media_type || null,
      is_active: config.is_active !== false
    });
  };

  const handleSave = async (displayFormat) => {
    try {
      setSaving(true);
      const updateData = {
        base_price: formData.base_price,
        price_per_day: formData.price_per_day || null,
        price_per_click: formData.price_per_click || null,
        price_per_view: formData.price_per_view || null,
        price_per_second: formData.price_per_second || null,
        min_duration_seconds: formData.min_duration_seconds,
        max_duration_seconds: formData.max_duration_seconds,
        pricing_mode: formData.pricing_mode,
        description: formData.description || null,
        media_type: formData.media_type || null,
        is_active: formData.is_active
      };

      await updateAdminPromotionPricingConfig(displayFormat, updateData);

      setEditingConfig(null);
      await fetchConfigs();
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(err?.message || 'Erreur lors de la mise à jour de la configuration');
    } finally {
      setSaving(false);
    }
  };

  const existingFormats = useMemo(
    () => new Set((configs || []).map((cfg) => cfg.display_format)),
    [configs]
  );

  const missingPresets = useMemo(
    () => DISPLAY_FORMAT_PRESETS.filter((preset) => !existingFormats.has(preset.display_format)),
    [existingFormats]
  );

  const handleCreatePreset = async (preset) => {
    try {
      setCreatingFormat(preset.display_format);
      setError(null);
      await createAdminPromotionPricingConfig({
        display_format: preset.display_format,
        ...preset.defaults,
      });
      await fetchConfigs();
    } catch (err) {
      console.error('Erreur création configuration:', err);
      setError(err?.message || `Erreur lors de la creation du format ${preset.label}`);
    } finally {
      setCreatingFormat('');
    }
  };

  const handleInitializeMissing = async () => {
    for (const preset of missingPresets) {
      await handleCreatePreset(preset);
    }
  };

  const handleCancel = () => {
    setEditingConfig(null);
  };

  const samplePreview = useMemo(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 6);
    return {
      startDate: start,
      endDate: end,
      displayDurationSeconds: 15,
      expectedClicks: 50,
      expectedViews: 200,
      mediaType: 'image',
      isFeatured: false
    };
  }, []);

  const formatLabels = {
    floating_left: 'Flottant à gauche',
    top_banner: 'Bande en haut',
    section: 'Section promotions'
  };

  const formatIcons = {
    floating_left: Image,
    top_banner: Video,
    section: Calculator
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Tarification des promotions
            </h1>
            <p className="text-gray-600">
              Définissez les prix selon le calendrier, la durée d&apos;affichage, l&apos;engagement (clics / vues) et le
              mode de combinaison — aligné sur le calcul serveur.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchConfigs}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {missingPresets.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Formats de promotion non configures</h2>
              <p className="text-sm text-gray-600">
                Creez les formats manquants pour pouvoir definir les tarifs de toutes les promos.
              </p>
            </div>
            <button
              type="button"
              onClick={handleInitializeMissing}
              disabled={creatingFormat !== ''}
              className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
            >
              Initialiser les formats manquants
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {missingPresets.map((preset) => (
              <button
                key={preset.display_format}
                type="button"
                onClick={() => handleCreatePreset(preset)}
                disabled={creatingFormat !== ''}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-amber-200 rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {creatingFormat === preset.display_format ? 'Creation...' : `Creer ${preset.label}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {configs.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune configuration trouvee</p>
            <p className="text-sm text-gray-500 mt-2">
              Utilisez les boutons ci-dessus pour creer les formats par defaut.
            </p>
          </div>
        ) : (
          configs.map((config) => {
            const isEditing = editingConfig === config.id;
            const FormatIcon = formatIcons[config.display_format] || Calculator;
            const previewConfig = isEditing ? { ...config, ...formData, display_format: config.display_format } : config;
            const exampleTotal = calculatePromotionServiceFee(previewConfig, samplePreview);
            const modeInfo = PRICING_MODES.find((m) => m.value === (isEditing ? formData.pricing_mode : config.pricing_mode)) || PRICING_MODES[0];

            return (
              <div
                key={config.id}
                className={`bg-white rounded-lg border-2 ${
                  config.is_active ? 'border-green-200' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <FormatIcon className="w-6 h-6 text-[#054625]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {formatLabels[config.display_format] || config.display_format}
                        </h3>
                        {(isEditing ? formData.description : config.description) && (
                          <p className="text-sm text-gray-500">
                            {isEditing ? formData.description : config.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          Mode : {modeInfo.label} — {modeInfo.hint}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {config.is_active ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                          Actif
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                          Inactif
                        </span>
                      )}
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={() => handleEdit(config)}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Modifier</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="space-y-6">
                      <div className="border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
                          <Calendar className="w-4 h-4 text-[#054625]" />
                          Calendrier (période de diffusion)
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix de base (1er jour)
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.base_price}
                                onChange={(e) =>
                                  setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })
                                }
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix par jour supplémentaire
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.price_per_day}
                                onChange={(e) =>
                                  setFormData({ ...formData, price_per_day: parseFloat(e.target.value) || 0 })
                                }
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
                          <Calculator className="w-4 h-4 text-[#054625]" />
                          Durée d&apos;affichage (animation / spot)
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Secondes couvertes par le forfait de base
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={formData.min_duration_seconds}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  min_duration_seconds: parseInt(e.target.value, 10) || 0
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix par seconde supplémentaire
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={formData.price_per_second}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    price_per_second: parseFloat(e.target.value) || 0
                                  })
                                }
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Durée max conseillée (UI)
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={formData.max_duration_seconds}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  max_duration_seconds: parseInt(e.target.value, 10) || 1
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
                          <MousePointerClick className="w-4 h-4 text-[#054625]" />
                          Engagement estimé
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix par clic
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.price_per_click}
                                onChange={(e) =>
                                  setFormData({ ...formData, price_per_click: parseFloat(e.target.value) || 0 })
                                }
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix par vue
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.price_per_view}
                                onChange={(e) =>
                                  setFormData({ ...formData, price_per_view: parseFloat(e.target.value) || 0 })
                                }
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mode de tarification
                        </label>
                        <select
                          value={formData.pricing_mode}
                          onChange={(e) => setFormData({ ...formData, pricing_mode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {PRICING_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {PRICING_MODES.find((m) => m.value === formData.pricing_mode)?.hint}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Notes internes…"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`active-${config.id}`}
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="mr-2"
                        />
                        <label htmlFor={`active-${config.id}`} className="text-sm font-medium text-gray-700">
                          Configuration active
                        </label>
                      </div>

                      <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSave(config.display_format)}
                          disabled={saving}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
                        >
                          <Save className="w-4 h-4" />
                          <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Base + /jour
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatPrice(config.base_price)} + {formatPrice(config.price_per_day ?? 0)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-600 mb-1">Secondes (forfait / +1s)</div>
                          <div className="text-lg font-bold text-gray-900">
                            {config.min_duration_seconds ?? '—'}s /{' '}
                            {config.price_per_second != null ? formatPrice(config.price_per_second) : '—'}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                            <MousePointerClick className="w-3 h-3" /> Clic / vue
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {config.price_per_click != null ? formatPrice(config.price_per_click) : '—'} /{' '}
                            {config.price_per_view != null ? formatPrice(config.price_per_view) : '—'}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-600 mb-1">Plage durée (UI)</div>
                          <div className="text-lg font-bold text-gray-900">
                            {config.min_duration_seconds ?? '—'}s – {config.max_duration_seconds ?? '—'}s
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Exemple (7 jours, 15 s d&apos;animation, 50 clics, 200 vues — image, non mise en avant)
                        </h4>
                        <div className="text-2xl font-bold text-green-700">{formatPrice(exampleTotal)}</div>
                        <p className="text-xs text-gray-600 mt-2">
                          Le montant réel dépend aussi des dates, durée, estimations et options choisies lors de la
                          création de la promotion côté restaurant (+25 % vidéo, +15 % mise en avant).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminPromotionPricing;
